import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { SignInWithApple } from "@capacitor-community/apple-sign-in";

const APPLE_SERVICES_ID = "com.fairwaylab.web";
const APPLE_REDIRECT_URI = "https://fairwaylabgolf.com/auth/apple/callback";

// Detect if running inside a Capacitor native shell (iOS/Android app)
const isNativeApp =
  typeof window !== "undefined" &&
  (window.Capacitor?.isNativePlatform?.() ||
    window.Capacitor?.getPlatform?.() === "ios" ||
    window.Capacitor?.getPlatform?.() === "android");

export default function Login() {
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) {
      navigate(user.has_profile ? "/home" : "/onboarding", { replace: true });
    }
  }, [user, loading, navigate]);

  // Load Apple JS SDK once on web (no need on native app)
  useEffect(() => {
    if (isNativeApp) return;
    if (document.querySelector("script[data-apple-sdk]")) return;
    const s = document.createElement("script");
    s.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    s.async = true;
    s.setAttribute("data-apple-sdk", "1");
    s.onload = () => {
      if (window.AppleID) {
        window.AppleID.auth.init({
          clientId: APPLE_SERVICES_ID,
          scope: "name email",
          redirectURI: APPLE_REDIRECT_URI,
          usePopup: true,
        });
      }
    };
    document.head.appendChild(s);
  }, []);

  const finishLogin = useCallback(async (identityToken, displayName) => {
    const res = await fetch(`${API}/auth/apple/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ identity_token: identityToken, name: displayName || null }),
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || `Login failed (${res.status})`);
    }
    const data = await res.json();
    // Backend set the session cookie; refresh AuthContext to pull /auth/me
    if (data?.session_token) {
      try { localStorage.setItem("session_token", data.session_token); } catch (e) { /* ignore */ }
    }
    await refresh();
  }, [refresh]);

  const startAppleAuthWeb = async () => {
    setError("");
    setBusy(true);
    try {
      if (!window.AppleID) throw new Error("Apple sign-in isn't ready yet — please refresh and try again.");
      const resp = await window.AppleID.auth.signIn();
      const idToken = resp?.authorization?.id_token;
      const name = resp?.user?.name ? `${resp.user.name.firstName || ""} ${resp.user.name.lastName || ""}`.trim() : null;
      if (!idToken) throw new Error("Apple did not return an identity token.");
      await finishLogin(idToken, name);
    } catch (e) {
      // User cancelled = popup_closed_by_user; ignore silently
      if (String(e?.error || "").includes("popup_closed_by_user")) {
        setBusy(false);
        return;
      }
      setError(e?.message || "Sign in failed. Please try again.");
      setBusy(false);
    }
  };

  const startAppleAuthNative = async () => {
    setError("");
    setBusy(true);
    try {
      const options = {
        clientId: "com.fairwaylab.app",
        redirectURI: APPLE_REDIRECT_URI,
        scopes: "email name",
      };
      const result = await SignInWithApple.authorize(options);
      const idToken = result?.response?.identityToken;
      const name = result?.response?.givenName
        ? `${result.response.givenName} ${result.response.familyName || ""}`.trim()
        : null;
      if (!idToken) throw new Error("Apple did not return an identity token.");
      await finishLogin(idToken, name);
    } catch (e) {
      if (String(e?.message || "").toLowerCase().includes("cancel")) {
        setBusy(false);
        return;
      }
      setError(e?.message || "Sign in failed. Please try again.");
      setBusy(false);
    }
  };

  const startAppleAuth = isNativeApp ? startAppleAuthNative : startAppleAuthWeb;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="max-w-3xl mx-auto w-full px-4 sm:px-6 h-16 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5" data-testid="login-logo">
          <Logo size={36} />
          <span className="font-heading font-black tracking-tight text-lg">Fairway Lab</span>
        </button>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 pb-32">
        <div className="w-full max-w-md text-center">
          <p className="chip mb-6 mx-auto">Sign in</p>
          <h1 className="h-display text-4xl sm:text-5xl leading-tight">
            Practice with intent.<br />Track every rep.
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed">
            One tap with Apple and your sessions, streaks, and reflections follow you everywhere.
          </p>
          <button
            onClick={startAppleAuth}
            disabled={busy}
            data-testid="login-apple-btn"
            className="mt-8 w-full inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-black text-white font-bold text-base hover:bg-neutral-800 active:scale-[0.98] transition-all disabled:opacity-60"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#FFFFFF" d="M16.365 1.43c0 1.14-.42 2.2-1.11 3-.75.87-1.96 1.55-3.14 1.46-.14-1.12.42-2.28 1.13-3.03.8-.88 2.15-1.55 3.12-1.43zM20.5 17.42c-.55 1.27-.81 1.83-1.52 2.95-.99 1.55-2.38 3.49-4.11 3.51-1.53.02-1.93-.99-4.01-.98-2.08.01-2.51.99-4.04.98-1.73-.03-3.05-1.78-4.04-3.33-2.77-4.33-3.06-9.4-1.35-12.1 1.21-1.92 3.13-3.05 4.94-3.05 1.84 0 2.99 1 4.51 1 1.47 0 2.37-1 4.49-1 1.6 0 3.3.87 4.51 2.37-3.97 2.17-3.32 7.83.62 9.66z"/>
            </svg>
            {busy ? "Signing in..." : "Continue with Apple"}
            {!busy && <ArrowRight className="w-4 h-4" />}
          </button>
          {error && (
            <p className="mt-3 text-sm text-red-600" data-testid="login-error">{error}</p>
          )}
          <p className="mt-6 text-xs text-slate-500">
            By continuing you agree to use Fairway Lab as a personal practice tool. We never share your data.
          </p>
        </div>
      </main>
    </div>
  );
}
